import React from 'react'
import { useTranslation } from 'react-i18next'
import { useCountryConfig } from '../../hooks/useCountryConfig'
import { isEgyptCountry, isSaudiCountry } from '../../utils/currencyUtils'

interface CurrencyDisplayProps {
  className?: string
  alt?: string
  iconStyle?: React.CSSProperties
  /** Saudi img asset — used when not Egypt and no children. */
  src?: string
  /**
   * Existing Saudi renderer (img or inline SVG).
   * Kept as-is for pixel-identical Saudi Arabia.
   */
  children?: React.ReactNode
}

/**
 * Currency renderer driven by Settings (via CountryConfig).
 * - Egypt (EG / EGP): localized text ج.م / EGP — never the Saudi SVG.
 * - All other / unset (incl. Saudi SAR): existing Saudi Riyal renderer (pixel-identical).
 */
const CurrencyDisplay: React.FC<CurrencyDisplayProps> = ({
  className,
  alt = 'SAR',
  iconStyle,
  src = '/sar.svg',
  children,
}) => {
  const countryConfig = useCountryConfig()
  const { t } = useTranslation()

  if (isEgyptCountry(countryConfig)) {
    return (
      <span className={className} style={{ display: 'inline-block', ...iconStyle }}>
        {t('currency.egyptSymbol')}
      </span>
    )
  }

  if (isSaudiCountry(countryConfig)) {
    if (children) return <>{children}</>
    return (
      <img
        src={src}
        alt={alt}
        className={className}
        style={iconStyle}
      />
    )
  }

  return (
    <span className={className} style={{ display: 'inline-block', ...iconStyle }}>
      {countryConfig.currency || alt}
    </span>
  )
}

export default CurrencyDisplay
